import { useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import LoadingContainer from 'src/componentes/loading-container';
import css from 'src/utils/css';
import FormularioGlobalAddMenu from './FormularioGlobalAddMenu';
import FormularioGlobalBufferedInput from './FormularioGlobalBufferedInput';
import FormularioGlobalQuestionCard from './FormularioGlobalQuestionCard';
import { normalizeStoredImageSize } from './FormularioGlobalResizableImage';
import FormularioGlobalSection from './FormularioGlobalSection';
import TipoFormularioIdentificacao from './TipoFormularioIdentificacao';
import {
  createFormularioGlobalQuestion,
  cloneQuestionMatrixSettings,
  getQuestionOptionsOrDefault,
  normalizeFormularioGlobalQuestionType,
  normalizeQuestionCommentSettings,
  normalizeQuestionMatrixSettings,
  QUESTION_TYPES_WITH_OPTIONS,
} from './FormularioGlobalQuestionTypes';

const ROOT_ITEM_TYPE = {
  SECTION: 'section',
  QUESTION: 'question',
};

const ROOT_DROPPABLE_ID = 'formulario-global-root';
const ROOT_DROPPABLE_TYPE = 'FORMULARIO_GLOBAL_ROOT';
const SECTION_QUESTIONS_DROPPABLE_PREFIX =
  'formulario-global-section-questions-';
const CLIENT_ID_FIELD = '_formularioGlobalClientId';

let clientIdCounter = 0;

const createClientId = (prefix) => {
  clientIdCounter += 1;
  return `${prefix}-${clientIdCounter}`;
};

const classes = css`
  .container {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 18px;
  }

  .header {
    background: #ffffff;
    border-radius: 8px;
    border-top: 6px solid #0f7180;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
    padding: 22px 26px;
  }

  .headerTop {
    align-items: flex-start;
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .titleInput {
    flex: 1;
    min-width: min(220px, 100%);
  }

  .headerActions {
    align-items: center;
    display: flex;
    flex-shrink: 0;
    gap: 12px;
    padding-top: 31px;
  }

  @media (max-width: 600px) {
    .headerActions {
      justify-content: flex-end;
      padding-top: 0;
      width: 100%;
    }
  }

  .items {
    display: flex;
    flex-direction: column;
    gap: 18px;
    margin-top: 22px;
  }

  .empty {
    margin-top: 18px;
  }

  .blocked {
    background: #fff7ed;
    border: 1px solid #fed7aa;
    border-radius: 8px;
    color: #9a3412;
    font-size: 13px;
    margin-top: 14px;
    padding: 12px 14px;
  }

  .footer {
    margin-top: 18px;
  }
`;

const getDraggableStyle = (draggableStyle, isDragging) => ({
  ...draggableStyle,
  opacity: isDragging ? 0.55 : 1,
});

const getRootDraggableId = (item, index) =>
  `formulario-global-root-${item._rootItemType}-${
    item[CLIENT_ID_FIELD] || item.id || index
  }`;

const getSectionDroppableId = (section) =>
  `${SECTION_QUESTIONS_DROPPABLE_PREFIX}${
    section[CLIENT_ID_FIELD] || section.id || section.ordem
  }`;

const getSectionIndexFromDroppableId = (items, droppableId) =>
  items.findIndex(
    (item) =>
      item._rootItemType === ROOT_ITEM_TYPE.SECTION &&
      getSectionDroppableId(item) === droppableId
  );

const normalizeQuestion = (pergunta = {}, index, intl) => {
  const tipo = normalizeFormularioGlobalQuestionType(
    pergunta.tipo || pergunta.tipoPergunta
  );
  const hasOptions = QUESTION_TYPES_WITH_OPTIONS.includes(tipo);
  const opcoes = getQuestionOptionsOrDefault(tipo, pergunta.opcoes || [], intl);
  const imageSize = normalizeStoredImageSize(
    pergunta.imagemLargura,
    pergunta.imagemAltura
  );

  return normalizeQuestionMatrixSettings(
    normalizeQuestionCommentSettings({
      ...pergunta,
      [CLIENT_ID_FIELD]:
        pergunta[CLIENT_ID_FIELD] || createClientId('question'),
      id: pergunta.id || 0,
      titulo: pergunta.titulo ?? '',
      tipo,
      obrigatoria: !!pergunta.obrigatoria,
      permitirComentario: !!pergunta.permitirComentario,
      comentarioObrigatorio: !!pergunta.comentarioObrigatorio,
      arquivoSistemaId: pergunta.arquivoSistemaId || null,
      imagem: pergunta.imagem || null,
      imagemLargura: imageSize.width,
      imagemAltura: imageSize.height,
      opcoes: hasOptions ? opcoes : [],
      ordem: index + 1,
    })
  );
};

const stripRootType = (item) => {
  const nextItem = { ...item };
  delete nextItem._rootItemType;
  delete nextItem._sectionIndex;
  delete nextItem._questionIndex;
  return nextItem;
};

const syncQuestionOrder = (perguntas = [], intl) =>
  perguntas.map((pergunta, index) => normalizeQuestion(pergunta, index, intl));

const syncSectionQuestions = (secao, intl) => {
  const imageSize = normalizeStoredImageSize(
    secao.imagemLargura,
    secao.imagemAltura
  );

  return {
    ...secao,
    [CLIENT_ID_FIELD]: secao[CLIENT_ID_FIELD] || createClientId('section'),
    imagemLargura: imageSize.width,
    imagemAltura: imageSize.height,
    perguntas: syncQuestionOrder(secao.perguntas || [], intl),
  };
};

const createSection = (ordem) => ({
  [CLIENT_ID_FIELD]: createClientId('section'),
  id: 0,
  titulo: '',
  descricao: '',
  obrigatoria: false,
  imagem: null,
  imagemLargura: null,
  imagemAltura: null,
  perguntas: [],
  ordem,
});

const syncRootItemsOrder = (items = [], intl) =>
  items.map((item, index) => {
    const ordem = index + 1;

    if (item._rootItemType === ROOT_ITEM_TYPE.SECTION) {
      return {
        ...syncSectionQuestions(item, intl),
        ordem,
      };
    }

    return {
      ...normalizeQuestion(item, index, intl),
      _rootItemType: ROOT_ITEM_TYPE.QUESTION,
      ordem,
    };
  });

const getRootItems = (secoes = [], perguntas = [], intl) => {
  const items = [
    ...secoes.map((secao, sectionIndex) => ({
      ...syncSectionQuestions(secao, intl),
      _rootItemType: ROOT_ITEM_TYPE.SECTION,
      _sectionIndex: sectionIndex,
    })),
    ...perguntas.map((pergunta, questionIndex) => ({
      ...pergunta,
      _rootItemType: ROOT_ITEM_TYPE.QUESTION,
      _questionIndex: questionIndex,
    })),
  ];

  return syncRootItemsOrder(
    items.sort((a, b) => (a.ordem || 0) - (b.ordem || 0)),
    intl
  );
};

const splitRootItems = (items) => ({
  secoes: items
    .filter((item) => item._rootItemType === ROOT_ITEM_TYPE.SECTION)
    .map(stripRootType),
  perguntas: items
    .filter((item) => item._rootItemType === ROOT_ITEM_TYPE.QUESTION)
    .map(stripRootType),
});

const cloneQuestion = (question, ordem) => ({
  ...question,
  [CLIENT_ID_FIELD]: createClientId('question'),
  id: 0,
  opcoes: (question.opcoes || []).map((opcao) => ({ ...opcao, id: 0 })),
  ...cloneQuestionMatrixSettings(question),
  ordem,
});

const cloneRootItem = (item, ordem) => {
  if (item._rootItemType === ROOT_ITEM_TYPE.SECTION) {
    return {
      ...item,
      [CLIENT_ID_FIELD]: createClientId('section'),
      id: 0,
      perguntas: (item.perguntas || []).map((question, index) =>
        cloneQuestion(question, index + 1)
      ),
      ordem,
    };
  }

  return cloneQuestion(item, ordem);
};

const moveItem = (items, fromIndex, toIndex) => {
  if (fromIndex === toIndex) return items;

  const nextItems = [...items];
  const [item] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, item);

  return nextItems;
};

const FormularioGlobalBuilder = ({
  blockedMessage,
  disabled,
  exibirConfiguracaoComentario = true,
  exibirConfiguracaoComentarioOpcao = true,
  exibirTipoFormularioIdentificacao = true,
  headerActions,
  isLoading,
  onChange,
  tipoFormulario,
  validationErrors,
  value,
}) => {
  const intl = useIntl();
  const formulario = value || {
    tipoFormulario,
    titulo: '',
    secoes: [],
    perguntas: [],
  };
  const formularioRef = useRef(formulario);
  formularioRef.current = formulario;

  const rootItems = useMemo(
    () =>
      getRootItems(formulario.secoes || [], formulario.perguntas || [], intl),
    [formulario.perguntas, formulario.secoes, intl]
  );
  const rootItemsRef = useRef(rootItems);
  rootItemsRef.current = rootItems;

  const emitFormularioChange = useCallback(
    (nextValue) => {
      onChange &&
        onChange({
          ...nextValue,
          tipoFormulario: nextValue.tipoFormulario || tipoFormulario,
        });
    },
    [onChange, tipoFormulario]
  );

  const emitRootItemsChange = useCallback(
    (nextRootItems) => {
      const orderedRootItems = syncRootItemsOrder(nextRootItems, intl);
      const splitItems = splitRootItems(orderedRootItems);

      emitFormularioChange({
        ...formularioRef.current,
        secoes: splitItems.secoes,
        perguntas: splitItems.perguntas,
      });
    },
    [emitFormularioChange, intl]
  );

  const handleChangeTitle = useCallback(
    (titulo) => {
      emitFormularioChange({
        ...formularioRef.current,
        titulo,
        ...splitRootItems(rootItemsRef.current),
      });
    },
    [emitFormularioChange]
  );

  const handleAddSection = useCallback(() => {
    const currentRootItems = rootItemsRef.current;

    emitRootItemsChange([
      ...currentRootItems,
      {
        ...createSection(currentRootItems.length + 1),
        _rootItemType: ROOT_ITEM_TYPE.SECTION,
      },
    ]);
  }, [emitRootItemsChange]);

  const handleAddQuestion = useCallback(
    (tipo) => {
      const currentRootItems = rootItemsRef.current;

      emitRootItemsChange([
        ...currentRootItems,
        {
          ...createFormularioGlobalQuestion(
            tipo,
            currentRootItems.length + 1,
            intl
          ),
          _rootItemType: ROOT_ITEM_TYPE.QUESTION,
        },
      ]);
    },
    [emitRootItemsChange, intl]
  );

  const handleChangeRootItem = useCallback(
    (index, item) => {
      const nextRootItems = [...rootItemsRef.current];
      nextRootItems[index] = item;
      emitRootItemsChange(nextRootItems);
    },
    [emitRootItemsChange]
  );

  const handleCloneRootItem = useCallback(
    (index) => {
      const currentRootItems = rootItemsRef.current;
      const item = currentRootItems[index];
      const clonedItem = cloneRootItem(item, index + 2);
      const nextRootItems = [...currentRootItems];
      nextRootItems.splice(index + 1, 0, clonedItem);
      emitRootItemsChange(nextRootItems);
    },
    [emitRootItemsChange]
  );

  const handleRemoveRootItem = useCallback(
    (index) => {
      emitRootItemsChange(
        rootItemsRef.current.filter((_, currentIndex) => currentIndex !== index)
      );
    },
    [emitRootItemsChange]
  );

  const handleMoveRootItem = useCallback(
    (index, direction) => {
      const currentRootItems = rootItemsRef.current;
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= currentRootItems.length) return;

      const nextRootItems = [...currentRootItems];
      const current = nextRootItems[index];
      nextRootItems[index] = nextRootItems[targetIndex];
      nextRootItems[targetIndex] = current;

      emitRootItemsChange(nextRootItems);
    },
    [emitRootItemsChange]
  );

  const handleDragEnd = useCallback(
    ({ source, destination }) => {
      if (disabled || !destination) return;
      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      ) {
        return;
      }

      if (
        source.droppableId === ROOT_DROPPABLE_ID &&
        destination.droppableId === ROOT_DROPPABLE_ID
      ) {
        emitRootItemsChange(
          moveItem(rootItemsRef.current, source.index, destination.index)
        );
        return;
      }

      if (
        source.droppableId.startsWith(SECTION_QUESTIONS_DROPPABLE_PREFIX) &&
        source.droppableId === destination.droppableId
      ) {
        const currentRootItems = rootItemsRef.current;
        const sectionIndex = getSectionIndexFromDroppableId(
          currentRootItems,
          source.droppableId
        );
        const section = currentRootItems[sectionIndex];

        if (section?._rootItemType !== ROOT_ITEM_TYPE.SECTION) return;

        const nextRootItems = [...currentRootItems];
        nextRootItems[sectionIndex] = {
          ...section,
          perguntas: moveItem(
            section.perguntas || [],
            source.index,
            destination.index
          ),
        };

        emitRootItemsChange(nextRootItems);
      }
    },
    [disabled, emitRootItemsChange]
  );

  return (
    <LoadingContainer isLoading={isLoading}>
      <div className={classes.container}>
        <div className={classes.header}>
          <div className={classes.headerTop}>
            <div className={classes.titleInput}>
              <FormularioGlobalBufferedInput
                disabled={disabled}
                label={intl.formatMessage({
                  id: 'tituloFormulario',
                })}
                onCommit={handleChangeTitle}
                placeholder={intl.formatMessage({
                  id: 'labelFormularioSemTitulo',
                })}
                value={formulario.titulo}
              />
            </div>
            {(headerActions || exibirTipoFormularioIdentificacao) && (
              <div className={classes.headerActions}>
                {headerActions}
                {exibirTipoFormularioIdentificacao && (
                  <TipoFormularioIdentificacao
                    tipoFormulario={tipoFormulario}
                  />
                )}
              </div>
            )}
          </div>
          {blockedMessage && (
            <div className={classes.blocked}>{blockedMessage}</div>
          )}
        </div>
        {rootItems.length > 0 ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable
              droppableId={ROOT_DROPPABLE_ID}
              isDropDisabled={disabled}
              type={ROOT_DROPPABLE_TYPE}
            >
              {(provided) => (
                <div
                  className={classes.items}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {rootItems.map((item, index) => (
                    <Draggable
                      draggableId={getRootDraggableId(item, index)}
                      index={index}
                      isDragDisabled={disabled}
                      key={getRootDraggableId(item, index)}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={getDraggableStyle(
                            provided.draggableProps.style,
                            snapshot.isDragging
                          )}
                        >
                          {item._rootItemType === ROOT_ITEM_TYPE.SECTION ? (
                            <FormularioGlobalSection
                              disabled={disabled}
                              dragHandleProps={provided.dragHandleProps}
                              isFirst={index === 0}
                              isDragging={snapshot.isDragging}
                              isLast={index === rootItems.length - 1}
                              onChange={(nextSection) =>
                                handleChangeRootItem(index, {
                                  ...nextSection,
                                  _rootItemType: ROOT_ITEM_TYPE.SECTION,
                                })
                              }
                              onClone={() => handleCloneRootItem(index)}
                              onMoveDown={() => handleMoveRootItem(index, 1)}
                              onMoveUp={() => handleMoveRootItem(index, -1)}
                              onRemove={() => handleRemoveRootItem(index)}
                              section={item}
                              sectionDroppableId={getSectionDroppableId(item)}
                              exibirConfiguracaoComentario={
                                exibirConfiguracaoComentario
                              }
                              exibirConfiguracaoComentarioOpcao={
                                exibirConfiguracaoComentarioOpcao
                              }
                              total={rootItems.length}
                              validationErrors={
                                validationErrors?.sections?.[item._sectionIndex]
                              }
                            />
                          ) : (
                            <FormularioGlobalQuestionCard
                              disabled={disabled}
                              dragHandleProps={provided.dragHandleProps}
                              isFirst={index === 0}
                              isDragging={snapshot.isDragging}
                              isLast={index === rootItems.length - 1}
                              onChange={(nextQuestion) =>
                                handleChangeRootItem(index, {
                                  ...nextQuestion,
                                  _rootItemType: ROOT_ITEM_TYPE.QUESTION,
                                })
                              }
                              onClone={() => handleCloneRootItem(index)}
                              onMoveDown={() => handleMoveRootItem(index, 1)}
                              onMoveUp={() => handleMoveRootItem(index, -1)}
                              onRemove={() => handleRemoveRootItem(index)}
                              question={item}
                              exibirConfiguracaoComentario={
                                exibirConfiguracaoComentario
                              }
                              exibirConfiguracaoComentarioOpcao={
                                exibirConfiguracaoComentarioOpcao
                              }
                              scopeId="root"
                              validationErrors={
                                validationErrors?.questions?.[
                                  item._questionIndex
                                ]
                              }
                            />
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <div className={classes.empty}>
            <FormularioGlobalAddMenu
              disabled={disabled}
              includeSection
              onAddQuestion={handleAddQuestion}
              onAddSection={handleAddSection}
            />
          </div>
        )}
        {rootItems.length > 0 && (
          <div className={classes.footer}>
            <FormularioGlobalAddMenu
              disabled={disabled}
              includeSection
              onAddQuestion={handleAddQuestion}
              onAddSection={handleAddSection}
            />
          </div>
        )}
      </div>
    </LoadingContainer>
  );
};

export default FormularioGlobalBuilder;
